import React, { useState, useCallback, useRef } from 'react';
import theme, { getTheme } from '../../theme';
import { useDarkMode } from '../../context/DarkModeContext';

export function ToastStack({ toasts, onDismiss }) {
  const { isDark } = useDarkMode();
  const t = getTheme(isDark);

  const KIND_CONFIG = {
    success: { color: t.green, bg: t.greenGlow, border: 'rgba(61,186,110,0.25)', icon: '✓' },
    error: { color: t.red, bg: t.redGlow, border: 'rgba(224,82,82,0.25)', icon: '✕' },
    info: { color: t.gold, bg: t.goldGlow, border: t.goldBorder, icon: 'ℹ' },
  };

  return (
    <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 2000, display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '340px' }}>
      {toasts.map(toast => {
        const cfg = KIND_CONFIG[toast.kind] || KIND_CONFIG.info;
        return (
          <div key={toast.id} role="status"
            style={{
              display: 'flex', alignItems: 'flex-start', gap: '10px',
              background: t.surface2,
              border: `1px solid ${cfg.border}`,
              padding: '12px 16px', borderRadius: theme.r2,
              boxShadow: t.shadow3, fontSize: 13,
              animation: 'ln-toast-in 0.2s ease',
              backdropFilter: 'blur(8px)',
            }}>
            <span style={{ color: cfg.color, fontWeight: 700, flexShrink: 0, fontSize: 14 }}>{cfg.icon}</span>
            <span style={{ flex: 1, lineHeight: 1.4, color: t.text }}>{toast.message}</span>
            <button onClick={() => onDismiss(toast.id)} aria-label="Dismiss"
              style={{ background: 'none', border: 'none', color: t.textTert, cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0, flexShrink: 0 }}>✕</button>
          </div>
        );
      })}
      <style>{`@keyframes ln-toast-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}

export function useToast() {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((message, kind = 'info', duration = 3500) => {
    const id = ++idRef.current;
    setToasts(prev => [...prev, { id, message, kind }]);
    if (duration > 0) setTimeout(() => dismissToast(id), duration);
  }, [dismissToast]);

  return { toasts, showToast, dismissToast };
}
